import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { FileText, Film, Image as ImageIcon, ExternalLink, Plus, ShieldCheck } from "lucide-react";
import VideoAnonymizer from "../components/VideoAnonymizer";

export default function Resources({ kind }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showAnonymizer, setShowAnonymizer] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Guías", type: "PDF", media_type: "video", url: "", description: "", pin: "" });
  const isMedia = kind === "multimedia";
  const canUpload = isMedia ? user.role === "SUPER_ADMIN" : ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR"].includes(user.role);
  const canFeature = !isMedia && ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR"].includes(user.role);

  const feature = async (rid, level) => {
    if (!level) return;
    try {
      await api.post(`/resources/${rid}/feature`, { level });
      toast.success("Recurso destacado para tus alumnos");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = () => api.get(isMedia ? "/multimedia" : "/resources").then((r) => setItems(r.data));
  useEffect(() => { load(); }, [kind]);

  const create = async () => {
    try {
      const body = isMedia
        ? { title: form.title, media_type: form.media_type, url: form.url, description: form.description }
        : { title: form.title, category: form.category, type: form.type, url: form.url, description: form.description };
      await api.post(isMedia ? "/multimedia" : "/resources", body, { headers: { "X-Master-Pin": form.pin } });
      toast.success("Contenido publicado");
      setShowForm(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = items.filter((i) =>
    (i.title + (i.category || "") + (i.description || "")).toLowerCase().includes(filter.toLowerCase()));

  const iconFor = (i) => isMedia
    ? (i.media_type === "video" ? <Film size={18} /> : <ImageIcon size={18} />)
    : <FileText size={18} />;

  return (
    <div className="fade-up" data-testid={isMedia ? "multimedia-page" : "resources-page"}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{isMedia ? "Biblioteca Multimedia" : "Biblioteca de Recursos"}</h1>
          <p className="text-neutral-500 mt-1">{isMedia ? "Vídeos, imágenes e infografías educativas." : "PDFs, guías, manuales y normativa DGT. Solo lectura."}</p>
        </div>
        <div className="flex gap-2">
          {isMedia && ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR"].includes(user.role) && (
            <Button data-testid="anonymize-video-btn" onClick={() => setShowAnonymizer(true)} variant="outline"
                    className="rounded-full border-neutral-300">
              <ShieldCheck size={15} className="mr-1.5 text-orange-600" /> Anonimizar Vídeo
            </Button>
          )}
          {canUpload && (
            <Button data-testid="resource-add-btn" onClick={() => setShowForm(!showForm)} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">
              <Plus size={15} className="mr-1.5" /> Añadir
            </Button>
          )}
        </div>
      </div>
      {isMedia && <VideoAnonymizer open={showAnonymizer} onClose={() => setShowAnonymizer(false)} />}

      {showForm && (
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 mb-6 grid md:grid-cols-2 gap-4" data-testid="resource-form">
          <Input placeholder="Título" data-testid="resource-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          {isMedia ? (
            <select value={form.media_type} onChange={(e) => setForm({ ...form, media_type: e.target.value })}
                    className="h-10 rounded-md border border-neutral-300 px-3 text-sm">
              <option value="video">Vídeo</option><option value="imagen">Imagen</option><option value="infografia">Infografía</option><option value="animacion">Animación</option>
            </select>
          ) : (
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="h-10 rounded-md border border-neutral-300 px-3 text-sm">
              {["Guías", "Manuales", "Normativa", "Procedimientos", "Documentos DGT", "Recursos internos"].map((c) => <option key={c}>{c}</option>)}
            </select>
          )}
          <Input placeholder="URL del documento" data-testid="resource-url-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
          <Input placeholder="Descripción" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Input placeholder="PIN Maestro" type="password" data-testid="resource-pin-input" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} />
          <Button data-testid="resource-submit-btn" onClick={create} className="bg-neutral-900 text-white rounded-full">Publicar</Button>
        </div>
      )}

      <Input data-testid="resource-search-input" placeholder="Buscar por título, categoría o temática…" value={filter}
             onChange={(e) => setFilter(e.target.value)} className="mb-6 max-w-md" />

      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm divide-y divide-neutral-100">
        {filtered.length === 0 && <p className="p-6 text-sm text-neutral-500">No hay contenidos que coincidan.</p>}
        {filtered.map((i, idx) => (
          <div key={i.id} className={`flex items-center gap-4 p-4 ${idx % 2 ? "bg-neutral-50/50" : ""}`} data-testid="resource-row">
            <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-700 flex items-center justify-center shrink-0">{iconFor(i)}</div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{i.title}</p>
              <p className="text-xs text-neutral-500 truncate">
                {(isMedia ? i.media_type : i.category)} · {fmtDate(i.date)}{i.author_name ? ` · ${i.author_name}` : ""} — {i.description}
              </p>
            </div>
            {canFeature && (
              <select data-testid="resource-feature-select" defaultValue="" onChange={(e) => { feature(i.id, e.target.value); e.target.value = ""; }}
                      className="h-8 rounded-md border border-neutral-300 px-2 text-xs text-neutral-600 shrink-0">
                <option value="" disabled>Destacar…</option>
                <option value="obligatorio">Obligatorio</option>
                <option value="recomendado">Recomendado</option>
                <option value="muy_recomendado">Muy recomendado</option>
                <option value="preparacion_examen">Preparación examen</option>
              </select>
            )}
            <a href={i.url} target="_blank" rel="noreferrer" data-testid="resource-open-link"
               className="text-orange-700 hover:text-orange-900 flex items-center gap-1 text-sm shrink-0">
              Abrir <ExternalLink size={13} />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}
